import { useEffect, useState, useRef, useMemo } from 'react';
import { formatDateForDisplay } from '../utils/dateUtils';
import { fetchWithCorpId } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.35, marginLeft: 4 }}><path d="M12 5v14M5 12l7-7 7 7"/></svg>;
  return sortConfig.direction === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 19V5M5 12l7-7 7 7"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" style={{ marginLeft: 4 }}><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
};

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = s === 'approved' ? 'badge badge-success'
    : s === 'rejected' ? 'badge badge-danger'
    : s === 'submitted' ? 'badge badge-warning'
    : s === 'locked' ? 'badge badge-neutral'
    : 'badge badge-neutral';
  return <span className={cls}>{status}</span>;
};

function TimesheetTable({ onEdit, onView }) {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [filteredTimesheets, setFilteredTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ employeeName: '', vendorName: '', status: [] });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const menuRefs = useRef({});
  const statusDropdownRef = useRef(null);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusOptions = useMemo(() => {
    const set = new Set();
    (timesheets || []).forEach(ts => { if (ts?.status) set.add(ts.status); });
    return Array.from(set).sort();
  }, [timesheets]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchTimesheets();
    const close = (e) => {
      if (openActionMenu) {
        const ref = menuRefs.current[openActionMenu];
        if (ref && !ref.contains(e.target)) setOpenActionMenu(null);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [page, size]);

  useEffect(() => { applyFilters(); }, [filter, timesheets]);
  useEffect(() => { applySorting(); }, [sortConfig]);

  const fetchTimesheets = async () => {
    try {
      const data = await fetchWithCorpId(`/timesheets?page=${page}&size=${size}`);
      if (Array.isArray(data)) {
        setTimesheets(data); setFilteredTimesheets(data); setTotalElements(data.length); setTotalPages(1);
      } else {
        const { content = [], totalElements: total = 0, totalPages: totalP = 0 } = data || {};
        setTimesheets(content); setFilteredTimesheets(content);
        setTotalElements(total); setTotalPages(totalP || (content.length ? 1 : 0));
      }
    } catch { setError('Failed to load timesheets');
    } finally { setLoading(false); }
  };

  const applyFilters = () => {
    setFilteredTimesheets(timesheets.filter(ts =>
      (!filter.employeeName || ts.employeeName?.toLowerCase().includes(filter.employeeName.toLowerCase())) &&
      (!filter.vendorName || ts.vendorName?.toLowerCase().includes(filter.vendorName.toLowerCase())) &&
      (filter.status.length === 0 || filter.status.includes(ts.status))
    ));
  };

  const applySorting = () => {
    if (!sortConfig.key) return;
    setFilteredTimesheets(prev => [...prev].sort((a, b) => {
      const vA = a[sortConfig.key], vB = b[sortConfig.key];
      if (vA == null) return 1; if (vB == null) return -1;
      if (typeof vA === 'number') return sortConfig.direction === 'asc' ? vA - vB : vB - vA;
      return sortConfig.direction === 'asc' ? vA.toString().localeCompare(vB.toString()) : vB.toString().localeCompare(vA.toString());
    }));
  };

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleFilterChange = (e) => setFilter(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleStatusCheckboxChange = (status) => {
    setFilter(prev => {
      const exists = prev.status.includes(status);
      return { ...prev, status: exists ? prev.status.filter(s => s !== status) : [...prev.status, status] };
    });
  };
  const handleChangePageSize = (e) => { setSize(parseInt(e.target.value, 10)); setPage(0); };

  const handleStatusChange = async (timesheetId, newStatus) => {
    try {
      await fetchWithCorpId(`/timesheets/${timesheetId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      fetchTimesheets();
    } catch { alert('Failed to update timesheet status'); }
  };

  const handleAction = async (type, timesheet) => {
    setOpenActionMenu(null);
    if (type === 'View') { onView(timesheet); return; }
    const statusMap = { Accept: 'APPROVED', Reject: 'REJECTED', Invoice: 'LOCKED' };
    if (statusMap[type]) {
      try { await handleStatusChange(timesheet.id, statusMap[type]); }
      catch (e) { alert(`Failed: ${e.message}`); }
    }
  };

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredTimesheets.map(ts => ({
      'Timesheet ID': ts.id, Employee: ts.employeeName, Vendor: ts.vendorName,
      'Billing Code': ts.billingCode, 'Start Date': formatDateForDisplay(ts.periodStartDate),
      'End Date': formatDateForDisplay(ts.periodEndDate), 'Total Hours': ts.totalHours,
      Status: ts.status, Notes: ts.notes,
    })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Timesheets');
    XLSX.writeFile(wb, 'timesheets.xlsx');
  };

  return (
    <div className="page-container">
      <div className="table-toolbar">
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Timesheets</h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>Track and manage employee timesheets</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-info" onClick={handleDownloadExcel}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button className="btn-primary" onClick={() => navigate('/on/timesheets/new')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Timesheet
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <input name="employeeName" placeholder="Employee name" value={filter.employeeName} onChange={handleFilterChange} />
        <input name="vendorName" placeholder="Vendor name" value={filter.vendorName} onChange={handleFilterChange} />
        <div ref={statusDropdownRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setIsStatusDropdownOpen(v => !v)}
            style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
          >
            {filter.status.length === 0 ? 'All Statuses' : `${filter.status.length} selected`}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          {isStatusDropdownOpen && (
            <div style={{ position: 'absolute', top: '110%', left: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, padding: '8px', minWidth: '180px' }}>
              {statusOptions.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: '13px', padding: '4px' }}>No statuses</div>
              ) : statusOptions.map(st => (
                <label key={st} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 4px', cursor: 'pointer', fontSize: '13px' }}>
                  <input type="checkbox" checked={filter.status.includes(st)} onChange={() => handleStatusCheckboxChange(st)} style={{ width: 'auto' }} />
                  {st}
                </label>
              ))}
              {filter.status.length > 0 && (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '6px' }}>
                  <button type="button" onClick={() => setFilter(p => ({ ...p, status: [] }))}
                    style={{ flex: 1, padding: '4px', border: '1px solid #e2e8f0', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '12px' }}>
                    Clear
                  </button>
                  <button type="button" onClick={() => setIsStatusDropdownOpen(false)}
                    style={{ flex: 1, padding: '4px', border: 'none', borderRadius: '6px', background: '#3b82f6', color: 'white', cursor: 'pointer', fontSize: '12px' }}>
                    Done
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <button className="btn-secondary" onClick={() => setFilter({ employeeName: '', vendorName: '', status: [] })}>Clear</button>
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Loading timesheets…</div>
      ) : error ? (
        <div className="alert error">{error}</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('id')}>ID <SortIcon col="id" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('employeeName')}>Employee <SortIcon col="employeeName" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('vendorName')}>Vendor <SortIcon col="vendorName" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('billingCode')}>Billing Code <SortIcon col="billingCode" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('periodStartDate')}>Start <SortIcon col="periodStartDate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('periodEndDate')}>End <SortIcon col="periodEndDate" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('totalHours')}>Hours <SortIcon col="totalHours" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status <SortIcon col="status" sortConfig={sortConfig} /></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('notes')}>Notes <SortIcon col="notes" sortConfig={sortConfig} /></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTimesheets.length === 0 ? (
                <tr className="state-row"><td colSpan="10">No timesheets found.</td></tr>
              ) : filteredTimesheets.map(ts => (
                <tr key={ts.id}>
                  <td><span className="table-link" onClick={() => navigate(`/on/timesheets/edit/${ts.id}`)}>{ts.id}</span></td>
                  <td style={{ fontWeight: '500' }}>{ts.employeeName}</td>
                  <td style={{ color: '#64748b' }}>{ts.vendorName}</td>
                  <td><code style={{ background: '#f1f5f9', padding: '2px 7px', borderRadius: '5px', fontSize: '12.5px' }}>{ts.billingCode}</code></td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{formatDateForDisplay(ts.periodStartDate)}</td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{formatDateForDisplay(ts.periodEndDate)}</td>
                  <td style={{ fontWeight: '600', color: '#1e293b' }}>{ts.totalHours}</td>
                  <td><StatusBadge status={ts.status} /></td>
                  <td style={{ color: '#64748b', fontSize: '13px', maxWidth: '200px' }}>{ts.notes}</td>
                  <td style={{ position: 'relative' }} ref={el => menuRefs.current[ts.id] = el}>
                    <button
                      onClick={() => setOpenActionMenu(openActionMenu === ts.id ? null : ts.id)}
                      style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
                    </button>
                    {openActionMenu === ts.id && (
                      <div className="action-menu">
                        <div className="action-menu-item" onClick={() => handleAction('View', ts)}>View</div>
                        {ts.status === 'Submitted' && (<>
                          <div className="action-menu-item" onClick={() => handleAction('Accept', ts)}>Accept</div>
                          <div className="action-menu-item" onClick={() => handleAction('Reject', ts)}>Reject</div>
                        </>)}
                        {ts.status === 'Approved' && (
                          <div className="action-menu-item" onClick={() => handleAction('Invoice', ts)}>Invoice</div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <span style={{ fontSize: '13px', color: '#64748b', marginRight: 'auto' }}>
              {typeof totalElements === 'number' ? `${totalElements} total` : ''}
            </span>
            <label style={{ fontSize: '13px', color: '#64748b' }}>Rows:&nbsp;
              <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '3px 6px' }}>
                {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimesheetTable;
